import { useEffect, useMemo, useRef, useState } from "react";
import { CLOUDINARY_INTEL_BASE_URL } from "@utils/enum";

type Workspace = {
  id?: string | number;
  name?: string;
  companies_count?: number;
};

type WorkspaceTabsProps = {
  workspaceList?: Workspace[];
  activeWorkspace?: Workspace;
  onWorkspaceSelect?: (workspace: Workspace) => void;
  onDeleteWorkspace?: (workspace: Workspace) => void;
};

const getWorkspaceId = (workspace?: Workspace) => workspace?.id;

const WorkspaceTabs = ({ workspaceList = [], activeWorkspace = {}, onWorkspaceSelect, onDeleteWorkspace }: WorkspaceTabsProps) => {
  const [showWorkspaceTabsMenu, setShowWorkspaceTabsMenu] = useState(false);
  const [tabsOverflow, setTabsOverflow] = useState(false);
  const [disableArrow, setDisableArrow] = useState({ left: true, right: true });
  const workspaceTabsMenuRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeWorkspaceId = getWorkspaceId(activeWorkspace);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (workspaceTabsMenuRef.current && !workspaceTabsMenuRef.current.contains(event.target as Node)) {
        setShowWorkspaceTabsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedWorkspaceList = useMemo(() => (Array.isArray(workspaceList) ? workspaceList : []), [workspaceList]);

  const updateOverflowAndArrows = () => {
    const element = scrollRef.current;
    if (!element) {
      setTabsOverflow(false);
      return;
    }
    const canScroll = element.scrollWidth > element.clientWidth + 1;
    setTabsOverflow(canScroll);
    if (!canScroll) {
      setDisableArrow({ left: true, right: true });
      setShowWorkspaceTabsMenu(false);
      return;
    }
    const leftDisabled = element.scrollLeft <= 0;
    const rightDisabled = element.scrollLeft + element.clientWidth >= element.scrollWidth - 1;
    setDisableArrow({ left: leftDisabled, right: rightDisabled });
  };

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      updateOverflowAndArrows();
    });
    return () => cancelAnimationFrame(raf);
  }, [normalizedWorkspaceList.length]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const activeElement = element.querySelector(`[data-workspace-id="${activeWorkspaceId}"]`);
    if (activeElement?.scrollIntoView) {
      activeElement.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
    const t = requestAnimationFrame(() => {
      updateOverflowAndArrows();
    });
    return () => cancelAnimationFrame(t);
  }, [activeWorkspaceId]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      updateOverflowAndArrows();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [normalizedWorkspaceList.length]);

  const handleScroll = (direction: "left" | "right") => {
    const element = scrollRef.current;
    if (!element) return;
    const scrollOffset = 260;
    const nextLeft = direction === "left" ? element.scrollLeft - scrollOffset : element.scrollLeft + scrollOffset;
    element.scrollTo({ left: nextLeft, behavior: "smooth" });
    setTimeout(() => {
      updateOverflowAndArrows();
    }, 240);
  };

  return (
    <div className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-2 tw-my-[10px]">
      {normalizedWorkspaceList.length > 0 && (
        <>
          {tabsOverflow && (
            <div className="tw-relative" ref={workspaceTabsMenuRef}>
              <button className="tw-h-9 tw-w-9 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-flex tw-items-center tw-justify-center hover:tw-bg-gray-50" onClick={() => setShowWorkspaceTabsMenu((prev) => !prev)}>
                <span className="material-symbols-outlined tw-text-lg">menu</span>
              </button>
              {showWorkspaceTabsMenu && (
                <div className="tw-absolute tw-top-[calc(100%+8px)] tw-left-0 tw-z-40 tw-w-[280px] tw-max-h-[320px] tw-overflow-y-auto tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg">
                  {normalizedWorkspaceList.map((list) => {
                    const workspaceId = getWorkspaceId(list);
                    const isActive = activeWorkspaceId === workspaceId;
                    return (
                      <button
                        key={`${workspaceId}-menu`}
                        className={`tw-w-full tw-border-0 tw-bg-transparent tw-text-left tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 last:tw-border-b-0 ${isActive ? "!tw-bg-primary-purple-600/10" : "hover:tw-bg-gray-50"}`}
                        onClick={() => {
                          onWorkspaceSelect?.(list);
                          setShowWorkspaceTabsMenu(false);
                        }}>
                        <div className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">{list?.name || "Workspace"}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tabsOverflow && !disableArrow.left && (
            <button className="tw-h-9 tw-w-9 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-flex tw-items-center tw-justify-center disabled:tw-opacity-50 disabled:tw-cursor-not-allowed hover:tw-bg-gray-50" onClick={() => handleScroll("left")} disabled={disableArrow.left}>
              <span className="material-symbols-outlined tw-text-lg">chevron_left</span>
            </button>
          )}

          <div ref={scrollRef} className="tw-flex-1 tw-min-w-0 tw-overflow-x-auto tw-overflow-y-hidden tw-scroll-smooth tw-no-scrollbar" onScroll={updateOverflowAndArrows}>
            <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-max">
              {normalizedWorkspaceList.map((list) => {
                const workspaceId = getWorkspaceId(list);
                const isActive = activeWorkspaceId === workspaceId;
                const companyCount = list?.companies_count || 0;
                return (
                  <button key={workspaceId} data-workspace-id={workspaceId} className={`tw-p-[2px] tw-rounded-[10px] ${isActive ? "tw-bg-primary-purple-600/10 " : "tw-bg-white"}`} onClick={() => onWorkspaceSelect?.(list)}>
                    <div className={`tw-flex tw-cursor-pointer tw-min-w-[224px] tw-flex-col tw-items-start tw-gap-1.5 tw-border tw-rounded-lg tw-px-4 tw-py-2 tw-text-left ${isActive ? "tw-border-primary-purple-500" : "tw-border-gray-200"}`} onClick={() => onWorkspaceSelect?.(list)}>
                      <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-2 tw-text-sm tw-font-medium">
                        <div className="tw-flex tw-items-center tw-gap-1 tw-max-w-[190px]">
                          <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1777897801/intel/misc/Workspace_folder_icon_qoyzrs.svg`} alt="Workspace" className="tw-h-5" />
                          <span className="tw-truncate">{list?.name || "Workspace"}</span>
                        </div>
                        {onDeleteWorkspace && (
                          <span
                            role="button"
                            aria-label={`Delete ${list?.name || "workspace"}`}
                            className="material-symbols-outlined tw-text-lg tw-leading-none tw-text-gray-500 tw-cursor-pointer hover:tw-text-error-500"
                            onClick={(event) => {
                              event.stopPropagation();
                              onDeleteWorkspace(list);
                            }}>
                            delete
                          </span>
                        )}
                      </div>
                      <div className="tw-text-base tw-font-semibold tw-text-gray-900 tw-leading-none">
                        <span className="tw-font-ibm-plex-mono">{companyCount}</span> <span className="tw-text-gray-500 tw-font-normal tw-text-sm">companies</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {tabsOverflow && !disableArrow.right && (
            <button className="tw-h-9 tw-w-9 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-flex tw-items-center tw-justify-center disabled:tw-opacity-50 disabled:tw-cursor-not-allowed hover:tw-bg-gray-50" onClick={() => handleScroll("right")} disabled={disableArrow.right}>
              <span className="material-symbols-outlined tw-text-lg">chevron_right</span>
            </button>
          )}
        </>
      )}
      <style>{`.tw-no-scrollbar::-webkit-scrollbar { display: none; } .tw-no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
    </div>
  );
};

export default WorkspaceTabs;
